import React, { useEffect, useMemo, useState } from 'react';
import {
  FaCopy,
  FaFileAlt,
  FaPlus,
  FaPrint,
  FaRedo,
  FaSave,
  FaSearch,
  FaTag,
  FaTrash,
  FaTruck,
} from 'react-icons/fa';
import './Freight.css';
import { showToast } from '../utils/toast';

const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
const API = API_BASE || '';

const LS_FREIGHT_TAB_KEY = 'freight.activeTab.v1';
const LS_FREIGHT_DRAFT_KEY = 'freight.draft.v1';
const LS_PARCEL_DRAFT_KEY = 'freight.parcelDraft.v1';
const LS_UNISHIPPERS_INTEGRATION_KEY = 'freight.unishippersIntegrationId.v1';

const todayYmd = () => new Date().toISOString().slice(0, 10);
const makeId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {}
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const emptyAddress = () => ({
  company: '',
  contactName: '',
  email: '',
  phone: '',
  country: 'United States',
  address1: '',
  address2: '',
  city: '',
  state: '',
  postalCode: '',
  residential: false,
});

const defaultCommodity = () => ({
  id: makeId(),
  name: '',
  freightClass: '',
  totalWeightLbs: '',
  nmfc: '',
  pieceType: '',
  pieces: '',
  description: '',
  hazmat: false,
});

const defaultHandlingUnit = () => ({
  id: makeId(),
  name: '',
  type: 'Pallet',
  units: 1,
  stackable: false,
  palletSize: '',
  lengthIn: '',
  widthIn: '',
  heightIn: '',
  commodities: [defaultCommodity()],
});

const defaultFreightDraft = () => ({
  shipDate: todayYmd(),
  skipAddressVerification: false,
  sender: emptyAddress(),
  receiver: emptyAddress(),
  addressNotes: '',
  handlingUnits: [defaultHandlingUnit()],
});

const defaultParcelDraft = () => ({
  shipDate: todayYmd(),
  skipAddressVerification: false,
  sender: emptyAddress(),
  receiver: emptyAddress(),
  additionalServices: {
    handlingCharge: false,
    carbonNeutral: false,
    returnLabel: false,
    shipmentAlerts: false,
    notifications: false,
    includeSendersReceipt: false,
    emailLabel: false,
  },
  signatureOption: 'No Signature Option',
  billingDetails: 'Bill My Account',
  package: {
    packageName: '',
    packageType: 'Custom',
    totalIdentical: 1,
    lengthIn: '',
    widthIn: '',
    heightIn: '',
    weightLbs: '',
    largePackage: false,
    additionalHandling: false,
    savePackage: false,
  },
  references: {
    ref1: '',
    ref2: '',
    printRef1Barcode: false,
  },
});

const readDraft = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const fetchJSON = async (url, options = {}) => {
  const res = await fetch(url, options);
  const text = await res.text();
  let json = null;
  try { json = text ? JSON.parse(text) : null; } catch {}
  if (!res.ok) {
    const msg = json?.error || json?.message || `${res.status} ${res.statusText}`;
    throw new Error(msg);
  }
  return json;
};

export default function Freight() {
  const [tab, setTab] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_FREIGHT_TAB_KEY);
      return raw === 'parcel' ? 'parcel' : 'freight';
    } catch {
      return 'freight';
    }
  });

  const [freight, setFreight] = useState(() => readDraft(LS_FREIGHT_DRAFT_KEY, defaultFreightDraft()));
  const [parcel, setParcel] = useState(() => readDraft(LS_PARCEL_DRAFT_KEY, defaultParcelDraft()));
  const [freightErrors, setFreightErrors] = useState({});
  const [parcelErrors, setParcelErrors] = useState({});
  const [freightQuoting, setFreightQuoting] = useState(false);
  const [parcelQuoting, setParcelQuoting] = useState(false);
  const [freightLastQuote, setFreightLastQuote] = useState(null);
  const [parcelLastQuote, setParcelLastQuote] = useState(null);
  const [unishippersProviders, setUnishippersProviders] = useState([]);
  const [unishippersIntegrationId, setUnishippersIntegrationId] = useState(() => {
    try { return localStorage.getItem(LS_UNISHIPPERS_INTEGRATION_KEY) || ''; } catch { return ''; }
  });

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Freight', subtitle: 'Quoting' }
    }));
    return () => window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
  }, []);

  useEffect(() => {
    try { localStorage.setItem(LS_FREIGHT_TAB_KEY, tab); } catch {}
  }, [tab]);

  useEffect(() => {
    try { localStorage.setItem(LS_UNISHIPPERS_INTEGRATION_KEY, String(unishippersIntegrationId || '')); } catch {}
  }, [unishippersIntegrationId]);

  useEffect(() => {
    try { localStorage.setItem(LS_FREIGHT_DRAFT_KEY, JSON.stringify(freight)); } catch {}
  }, [freight]);

  useEffect(() => {
    try { localStorage.setItem(LS_PARCEL_DRAFT_KEY, JSON.stringify(parcel)); } catch {}
  }, [parcel]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const j = await fetchJSON(`${API}/api/shippingintegrations/providers?enabled=1`);
        const providers = Array.isArray(j?.data) ? j.data : [];
        const uni = providers.filter((p) => String(p?.servicetype || '').toLowerCase().includes('unishippers'));
        if (!alive) return;
        setUnishippersProviders(uni);
        if (uni.length) {
          const current = String(unishippersIntegrationId || '').trim();
          const exists = current && uni.some((p) => String(p.id) === current);
          if (!exists) setUnishippersIntegrationId(String(uni[0].id));
        }
      } catch {
        if (!alive) return;
        setUnishippersProviders([]);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const freightSummary = useMemo(() => {
    const huCount = Array.isArray(freight?.handlingUnits) ? freight.handlingUnits.length : 0;
    const commodityCount = (freight?.handlingUnits || []).reduce((acc, hu) => acc + (Array.isArray(hu?.commodities) ? hu.commodities.length : 0), 0);
    const totalWeight = (freight?.handlingUnits || []).reduce((acc, hu) => {
      const sum = (hu?.commodities || []).reduce((a, c) => a + (Number(c?.totalWeightLbs) || 0), 0);
      return acc + sum;
    }, 0);
    return { huCount, commodityCount, totalWeight };
  }, [freight]);

  const setFreightAddrField = (which, key, value) => {
    setFreight((prev) => ({
      ...prev,
      [which]: { ...(prev?.[which] || emptyAddress()), [key]: value }
    }));
  };

  const setParcelAddrField = (which, key, value) => {
    setParcel((prev) => ({
      ...prev,
      [which]: { ...(prev?.[which] || emptyAddress()), [key]: value }
    }));
  };

  const updateHandlingUnit = (unitId, patch) => {
    setFreight((prev) => ({
      ...prev,
      handlingUnits: (prev?.handlingUnits || []).map((hu) => (hu.id === unitId ? { ...hu, ...patch } : hu))
    }));
  };

  const addHandlingUnit = () => {
    setFreight((prev) => ({ ...prev, handlingUnits: [...(prev?.handlingUnits || []), defaultHandlingUnit()] }));
  };

  const copyHandlingUnit = (unitId) => {
    setFreight((prev) => {
      const list = prev?.handlingUnits || [];
      const idx = list.findIndex((x) => x.id === unitId);
      if (idx === -1) return prev;
      const cloned = JSON.parse(JSON.stringify(list[idx] || {}));
      cloned.id = makeId();
      cloned.name = `${cloned.name || 'Handling Unit'} (copy)`;
      cloned.commodities = (cloned.commodities || []).map((c) => ({ ...c, id: makeId() }));
      const next = [...list.slice(0, idx + 1), cloned, ...list.slice(idx + 1)];
      return { ...prev, handlingUnits: next };
    });
    showToast({ title: 'Copied', message: 'Handling unit duplicated.', variant: 'info' });
  };

  const deleteHandlingUnit = (unitId) => {
    setFreight((prev) => {
      const next = (prev?.handlingUnits || []).filter((x) => x.id !== unitId);
      return { ...prev, handlingUnits: next.length ? next : [defaultHandlingUnit()] };
    });
  };

  const addCommodity = (unitId) => {
    setFreight((prev) => ({
      ...prev,
      handlingUnits: (prev?.handlingUnits || []).map((hu) => (
        hu.id === unitId ? { ...hu, commodities: [...(hu?.commodities || []), defaultCommodity()] } : hu
      ))
    }));
  };

  const updateCommodity = (unitId, commodityId, patch) => {
    setFreight((prev) => ({
      ...prev,
      handlingUnits: (prev?.handlingUnits || []).map((hu) => {
        if (hu.id !== unitId) return hu;
        return {
          ...hu,
          commodities: (hu?.commodities || []).map((c) => (c.id === commodityId ? { ...c, ...patch } : c))
        };
      })
    }));
  };

  const deleteCommodity = (unitId, commodityId) => {
    setFreight((prev) => ({
      ...prev,
      handlingUnits: (prev?.handlingUnits || []).map((hu) => {
        if (hu.id !== unitId) return hu;
        const next = (hu?.commodities || []).filter((c) => c.id !== commodityId);
        return { ...hu, commodities: next.length ? next : [defaultCommodity()] };
      })
    }));
  };

  const getErr = (errs, key) => (errs && typeof errs === 'object' ? errs[key] : '');

  const validateFreight = () => {
    const errs = {};
    const reqAddr = (prefix, addr) => {
      if (!String(addr?.company || '').trim()) errs[`${prefix}.company`] = 'Required';
      if (!String(addr?.postalCode || '').trim()) errs[`${prefix}.postalCode`] = 'Required';
      if (!String(addr?.state || '').trim()) errs[`${prefix}.state`] = 'Required';
      if (!String(addr?.city || '').trim()) errs[`${prefix}.city`] = 'Required';
    };
    reqAddr('sender', freight?.sender);
    reqAddr('receiver', freight?.receiver);

    (freight?.handlingUnits || []).forEach((hu) => {
      if (!String(hu?.lengthIn || '').trim()) errs[`hu.${hu.id}.lengthIn`] = 'Length is required';
      if (!String(hu?.widthIn || '').trim()) errs[`hu.${hu.id}.widthIn`] = 'Width is required';
      (hu?.commodities || []).forEach((c) => {
        if (!String(c?.freightClass || '').trim()) errs[`hu.${hu.id}.c.${c.id}.freightClass`] = 'Class is required';
        if (!String(c?.totalWeightLbs || '').trim()) errs[`hu.${hu.id}.c.${c.id}.totalWeightLbs`] = 'Weight is required';
      });
    });

    setFreightErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateParcel = () => {
    const errs = {};
    const addr = parcel?.receiver || {};
    if (!String(addr?.company || '').trim()) errs['receiver.company'] = 'Required';
    if (!String(addr?.postalCode || '').trim()) errs['receiver.postalCode'] = 'Required';
    if (!String(addr?.state || '').trim()) errs['receiver.state'] = 'Required';
    if (!String(addr?.city || '').trim()) errs['receiver.city'] = 'Required';

    const pkg = parcel?.package || {};
    if (!String(pkg?.lengthIn || '').trim()) errs['package.lengthIn'] = 'Required';
    if (!String(pkg?.widthIn || '').trim()) errs['package.widthIn'] = 'Required';
    if (!String(pkg?.heightIn || '').trim()) errs['package.heightIn'] = 'Required';
    if (!String(pkg?.weightLbs || '').trim()) errs['package.weightLbs'] = 'Required';

    setParcelErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleGetFreightQuote = () => {
    (async () => {
      if (!unishippersIntegrationId && !unishippersProviders.length) {
        showToast({ title: 'Unishippers not configured', message: 'Add an Unishippers provider in Shipping Integrations first.', variant: 'error' });
        return;
      }
      if (!validateFreight()) {
        showToast({ title: 'Fix required fields', message: 'Complete the highlighted fields before requesting a quote.', variant: 'error' });
        return;
      }
      setFreightQuoting(true);
      try {
        const result = await fetchJSON(`${API}/api/freight/quotes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productType: 'LTL', draft: freight, integrationId: unishippersIntegrationId ? Number(unishippersIntegrationId) : undefined }),
        });
        setFreightLastQuote(result);
        showToast({ title: 'Quote received', message: `Saved quote #${result.quoteId} (${result.offersCount || 0} offer(s)).`, variant: 'success' });
      } catch (e) {
        showToast({ title: 'Quote failed', message: e?.message || 'Unable to get a quote.', variant: 'error' });
      } finally {
        setFreightQuoting(false);
      }
    })();
  };

  const handleQuickParcelQuote = () => {
    (async () => {
      if (!unishippersIntegrationId && !unishippersProviders.length) {
        showToast({ title: 'Unishippers not configured', message: 'Add an Unishippers provider in Shipping Integrations first.', variant: 'error' });
        return;
      }
      if (!validateParcel()) {
        showToast({ title: 'Fix required fields', message: 'Complete the highlighted fields before requesting a quote.', variant: 'error' });
        return;
      }
      setParcelQuoting(true);
      try {
        const result = await fetchJSON(`${API}/api/freight/quotes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productType: 'SMALLPACK', draft: parcel, integrationId: unishippersIntegrationId ? Number(unishippersIntegrationId) : undefined }),
        });
        setParcelLastQuote(result);
        showToast({ title: 'Quote received', message: `Saved quote #${result.quoteId} (${result.offersCount || 0} offer(s)).`, variant: 'success' });
      } catch (e) {
        showToast({ title: 'Quote failed', message: e?.message || 'Unable to get a quote.', variant: 'error' });
      } finally {
        setParcelQuoting(false);
      }
    })();
  };

  const resetDraft = () => {
    if (tab === 'freight') {
      setFreight(defaultFreightDraft());
      setFreightErrors({});
      try { localStorage.removeItem(LS_FREIGHT_DRAFT_KEY); } catch {}
    } else {
      setParcel(defaultParcelDraft());
      setParcelErrors({});
      try { localStorage.removeItem(LS_PARCEL_DRAFT_KEY); } catch {}
    }
    showToast({ title: 'Reset', message: 'Draft cleared.', variant: 'info' });
  };

  const renderAddressBlock = (mode, which, addr, setField, errs) => {
    const prefix = `${which}.`;
    const required = mode === 'freight' ? true : which === 'receiver';
    return (
      <div className="freight-card">
        <div className="freight-card-header">
          <h3 style={{ margin: 0, fontSize: '1rem' }}>{which === 'sender' ? 'Sender' : 'Receiver'}</h3>
          <button
            type="button"
            className="btn secondary"
            onClick={() => {
              const next = emptyAddress();
              if (mode === 'freight') {
                setFreight((prev) => ({ ...prev, [which]: next }));
                setFreightErrors((prev) => {
                  const cleaned = { ...(prev || {}) };
                  Object.keys(cleaned).forEach((k) => { if (k.startsWith(prefix)) delete cleaned[k]; });
                  return cleaned;
                });
              } else {
                setParcel((prev) => ({ ...prev, [which]: next }));
                setParcelErrors((prev) => {
                  const cleaned = { ...(prev || {}) };
                  Object.keys(cleaned).forEach((k) => { if (k.startsWith(prefix)) delete cleaned[k]; });
                  return cleaned;
                });
              }
            }}
            title="Clear"
          >
            <FaRedo /> Clear All
          </button>
        </div>
        <div className="freight-card-body">
          <div className="freight-grid">
            <div className="freight-field" style={{ gridColumn: 'span 12' }}>
              <label>{which === 'sender' ? 'Company or Name' : 'Company or Name'}{required ? ' *' : ''}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className={`freight-input ${getErr(errs, `${which}.company`) ? 'freight-field-error' : ''}`}
                  value={addr.company || ''}
                  onChange={(e) => setField(which, 'company', e.target.value)}
                  placeholder="Company or Name"
                />
                <button type="button" className="btn secondary" title="Search (coming soon)" onClick={() => showToast({ title: 'Coming soon', message: 'Address search/autocomplete will be added.', variant: 'info' })}>
                  <FaSearch />
                </button>
              </div>
              {getErr(errs, `${which}.company`) && <div className="freight-error-text">{getErr(errs, `${which}.company`)}</div>}
            </div>

            <div className="freight-field" style={{ gridColumn: 'span 6' }}>
              <label>Country</label>
              <input className="freight-input" value={addr.country || ''} onChange={(e) => setField(which, 'country', e.target.value)} />
            </div>
            <div className="freight-field" style={{ gridColumn: 'span 3' }}>
              <label>Postal Code{required ? ' *' : ''}</label>
              <input
                className={`freight-input ${getErr(errs, `${which}.postalCode`) ? 'freight-field-error' : ''}`}
                value={addr.postalCode || ''}
                onChange={(e) => setField(which, 'postalCode', e.target.value)}
                placeholder="Postal Code"
              />
              {getErr(errs, `${which}.postalCode`) && <div className="freight-error-text">{getErr(errs, `${which}.postalCode`)}</div>}
            </div>
            <div className="freight-field" style={{ gridColumn: 'span 3' }}>
              <label>State{required ? ' *' : ''}</label>
              <input
                className={`freight-input ${getErr(errs, `${which}.state`) ? 'freight-field-error' : ''}`}
                value={addr.state || ''}
                onChange={(e) => setField(which, 'state', e.target.value)}
                placeholder="State"
              />
              {getErr(errs, `${which}.state`) && <div className="freight-error-text">{getErr(errs, `${which}.state`)}</div>}
            </div>
            <div className="freight-field" style={{ gridColumn: 'span 12' }}>
              <label>City{required ? ' *' : ''}</label>
              <input
                className={`freight-input ${getErr(errs, `${which}.city`) ? 'freight-field-error' : ''}`}
                value={addr.city || ''}
                onChange={(e) => setField(which, 'city', e.target.value)}
                placeholder="City"
              />
              {getErr(errs, `${which}.city`) && <div className="freight-error-text">{getErr(errs, `${which}.city`)}</div>}
            </div>
          </div>

          <details className="freight-collapsible" style={{ marginTop: 12 }}>
            <summary>Enter Full Address</summary>
            <div className="freight-card-body">
              <div className="freight-grid">
                <div className="freight-field" style={{ gridColumn: 'span 12' }}>
                  <label>Address 1 <span className="freight-muted">(optional)</span></label>
                  <input className="freight-input" value={addr.address1 || ''} onChange={(e) => setField(which, 'address1', e.target.value)} placeholder="Address 1" />
                </div>
                <div className="freight-field" style={{ gridColumn: 'span 12' }}>
                  <label>Address 2 <span className="freight-muted">(optional)</span></label>
                  <input className="freight-input" value={addr.address2 || ''} onChange={(e) => setField(which, 'address2', e.target.value)} placeholder="Address 2" />
                </div>
                <div className="freight-field" style={{ gridColumn: 'span 6' }}>
                  <label>Contact Name <span className="freight-muted">(optional)</span></label>
                  <input className="freight-input" value={addr.contactName || ''} onChange={(e) => setField(which, 'contactName', e.target.value)} placeholder="Contact Name" />
                </div>
                <div className="freight-field" style={{ gridColumn: 'span 6' }}>
                  <label>Email <span className="freight-muted">(optional)</span></label>
                  <input className="freight-input" value={addr.email || ''} onChange={(e) => setField(which, 'email', e.target.value)} placeholder="Email" />
                </div>
                <div className="freight-field" style={{ gridColumn: 'span 6' }}>
                  <label>Phone <span className="freight-muted">(optional)</span></label>
                  <input className="freight-input" value={addr.phone || ''} onChange={(e) => setField(which, 'phone', e.target.value)} placeholder="Phone number" />
                </div>
                <div className="freight-field" style={{ gridColumn: 'span 6' }}>
                  <label className="freight-pill" style={{ cursor: 'pointer', width: 'fit-content', marginTop: 22 }}>
                    <input
                      type="checkbox"
                      checked={!!addr.residential}
                      onChange={(e) => setField(which, 'residential', e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    Residential
                  </label>
                </div>
              </div>
            </div>
          </details>
        </div>
      </div>
    );
  };

  const freightPalletSizeOptions = [
    { value: '', label: 'Pallet Size (optional)' },
    { value: '48x40', label: '48 x 40' },
    { value: '48x48', label: '48 x 48' },
    { value: '60x48', label: '60 x 48' },
    { value: 'Other', label: 'Other' },
  ];

  const commodityClassOptions = ['', '50', '55', '60', '65', '70', '77.5', '85', '92.5', '100', '110', '125', '150', '175', '200', '250', '300', '400', '500'];

  const renderFreightTab = () => (
    <div className="freight-layout">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div className="freight-card">
          <div className="freight-card-header">
            <h2>What are you shipping?</h2>
            <div className="freight-row-actions">
              <span className="freight-pill">
                <FaTag /> Calculate Density (coming soon)
              </span>
            </div>
          </div>
          <div className="freight-card-body">
            <div className="freight-section-sub">
              <div className="freight-field" style={{ width: 'min(520px, 100%)' }}>
                <label>Handling Unit Name <span className="freight-muted">(optional)</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="freight-input" placeholder="Handling Unit Name" />
                  <button type="button" className="btn secondary" onClick={() => showToast({ title: 'Coming soon', message: 'Handling unit templates will be added.', variant: 'info' })}>
                    <FaSearch />
                  </button>
                </div>
              </div>
              <div className="freight-row-actions">
                <button type="button" className="btn secondary" onClick={addHandlingUnit}>
                  <FaPlus /> Handling Unit
                </button>
              </div>
            </div>

            {(freight?.handlingUnits || []).map((hu, idx) => (
              <div key={hu.id} style={{ marginTop: idx === 0 ? 0 : 18 }}>
                <div className="freight-section-sub" style={{ marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 800 }}>Handling Unit {idx + 1}</div>
                    <div className="freight-muted">Build the handling unit and its commodities.</div>
                  </div>
                  <div className="freight-row-actions">
                    <button type="button" className="btn secondary" onClick={() => copyHandlingUnit(hu.id)} title="Copy">
                      <FaCopy /> Copy
                    </button>
                    <button type="button" className="btn secondary" onClick={() => showToast({ title: 'Saved', message: 'Draft saved locally.', variant: 'info' })} title="Save">
                      <FaSave /> Save
                    </button>
                    <button type="button" className="btn secondary" onClick={addHandlingUnit} title="Add handling unit">
                      <FaPlus /> Handling Unit
                    </button>
                    <button type="button" className="btn secondary" onClick={() => deleteHandlingUnit(hu.id)} title="Delete handling unit">
                      <FaTrash />
                    </button>
                  </div>
                </div>

	                <div className="freight-grid">
	                  <div className="freight-field" style={{ gridColumn: 'span 2' }}>
	                    <label>Type</label>
	                    <select className="freight-select" value={hu.type || 'Pallet'} onChange={(e) => updateHandlingUnit(hu.id, { type: e.target.value })}>
	                      <option>Pallet</option>
	                      <option>Crate</option>
	                      <option>Box</option>
	                      <option>Drum</option>
	                    </select>
	                  </div>
	                  <div className="freight-field" style={{ gridColumn: 'span 2' }}>
	                    <label># of Units</label>
	                    <input
	                      className="freight-input"
	                      type="number"
	                      min={1}
                      value={hu.units ?? 1}
	                      onChange={(e) => updateHandlingUnit(hu.id, { units: Math.max(1, Number(e.target.value || 1)) })}
	                    />
	                  </div>
	                  <div className="freight-field" style={{ gridColumn: 'span 2' }}>
	                    <label>Stackable</label>
	                    <select className="freight-select" value={hu.stackable ? 'yes' : 'no'} onChange={(e) => updateHandlingUnit(hu.id, { stackable: e.target.value === 'yes' })}>
	                      <option value="no">No</option>
	                      <option value="yes">Yes</option>
	                    </select>
	                  </div>
	                  <div className="freight-field" style={{ gridColumn: 'span 2' }}>
	                    <label>Pallet Size <span className="freight-muted">(optional)</span></label>
	                    <select className="freight-select" value={hu.palletSize || ''} onChange={(e) => updateHandlingUnit(hu.id, { palletSize: e.target.value })}>
	                      {freightPalletSizeOptions.map((o) => (
	                        <option key={o.value} value={o.value}>{o.label}</option>
	                      ))}
	                    </select>
	                  </div>
	                  <div className="freight-dims-group" style={{ gridColumn: 'span 4' }}>
	                    <div className="freight-dims-grid">
	                      <div className="freight-field">
	                        <label>Length (in.) *</label>
	                        <input
	                          className={`freight-input ${getErr(freightErrors, `hu.${hu.id}.lengthIn`) ? 'freight-field-error' : ''}`}
	                          value={hu.lengthIn ?? ''}
	                          onChange={(e) => updateHandlingUnit(hu.id, { lengthIn: e.target.value })}
	                          placeholder="Length"
	                        />
	                        {getErr(freightErrors, `hu.${hu.id}.lengthIn`) && <div className="freight-error-text">{getErr(freightErrors, `hu.${hu.id}.lengthIn`)}</div>}
	                      </div>
	                      <div className="freight-field">
	                        <label>Width (in.) *</label>
	                        <input
	                          className={`freight-input ${getErr(freightErrors, `hu.${hu.id}.widthIn`) ? 'freight-field-error' : ''}`}
	                          value={hu.widthIn ?? ''}
	                          onChange={(e) => updateHandlingUnit(hu.id, { widthIn: e.target.value })}
	                          placeholder="Width"
	                        />
	                        {getErr(freightErrors, `hu.${hu.id}.widthIn`) && <div className="freight-error-text">{getErr(freightErrors, `hu.${hu.id}.widthIn`)}</div>}
	                      </div>
	                      <div className="freight-field">
	                        <label>Height (in.) <span className="freight-muted">(optional)</span></label>
	                        <input className="freight-input" value={hu.heightIn ?? ''} onChange={(e) => updateHandlingUnit(hu.id, { heightIn: e.target.value })} placeholder="Height" />
	                      </div>
	                    </div>
	                  </div>
	                </div>

                <div className="freight-divider" />

                {(hu?.commodities || []).map((c, cIdx) => (
                  <div key={c.id} style={{ marginTop: cIdx === 0 ? 0 : 14 }}>
                    <div className="freight-section-sub" style={{ marginBottom: 10 }}>
                      <div style={{ fontWeight: 800 }}>Commodity {cIdx + 1}</div>
                      <div className="freight-row-actions">
                        <label className="freight-pill" style={{ cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!c.hazmat}
                            onChange={(e) => updateCommodity(hu.id, c.id, { hazmat: e.target.checked })}
                            style={{ margin: 0 }}
                          />
                          Hazmat
                        </label>
                        <button type="button" className="btn secondary" onClick={() => addCommodity(hu.id)} title="Add commodity">
                          <FaPlus /> Commodity
                        </button>
                        <button type="button" className="btn secondary" onClick={() => deleteCommodity(hu.id, c.id)} title="Delete commodity">
                          <FaTrash />
                        </button>
                      </div>
                    </div>

                    <div className="freight-grid">
                      <div className="freight-field" style={{ gridColumn: 'span 4' }}>
                        <label>Commodity Name <span className="freight-muted">(optional)</span></label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input className="freight-input" value={c.name || ''} onChange={(e) => updateCommodity(hu.id, c.id, { name: e.target.value })} placeholder="Commodity Name" />
                          <button type="button" className="btn secondary" title="Search (coming soon)" onClick={() => showToast({ title: 'Coming soon', message: 'Commodity templates will be added.', variant: 'info' })}>
                            <FaSearch />
                          </button>
                        </div>
                      </div>
                      <div className="freight-field" style={{ gridColumn: 'span 2' }}>
                        <label>Class *</label>
                        <select
                          className={`freight-select ${getErr(freightErrors, `hu.${hu.id}.c.${c.id}.freightClass`) ? 'freight-field-error' : ''}`}
                          value={c.freightClass ?? ''}
                          onChange={(e) => updateCommodity(hu.id, c.id, { freightClass: e.target.value })}
                        >
                          <option value="">Select</option>
                          {commodityClassOptions.filter(Boolean).map((v) => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                        </select>
                        {getErr(freightErrors, `hu.${hu.id}.c.${c.id}.freightClass`) && <div className="freight-error-text">{getErr(freightErrors, `hu.${hu.id}.c.${c.id}.freightClass`)}</div>}
                      </div>
                      <div className="freight-field" style={{ gridColumn: 'span 2' }}>
                        <label>Total Weight (lbs) *</label>
                        <input
                          className={`freight-input ${getErr(freightErrors, `hu.${hu.id}.c.${c.id}.totalWeightLbs`) ? 'freight-field-error' : ''}`}
                          value={c.totalWeightLbs ?? ''}
                          onChange={(e) => updateCommodity(hu.id, c.id, { totalWeightLbs: e.target.value })}
                          placeholder="lbs"
                        />
                        {getErr(freightErrors, `hu.${hu.id}.c.${c.id}.totalWeightLbs`) && <div className="freight-error-text">{getErr(freightErrors, `hu.${hu.id}.c.${c.id}.totalWeightLbs`)}</div>}
                      </div>
                      <div className="freight-field" style={{ gridColumn: 'span 2' }}>
                        <label>NMFC <span className="freight-muted">(optional)</span></label>
                        <input className="freight-input" value={c.nmfc ?? ''} onChange={(e) => updateCommodity(hu.id, c.id, { nmfc: e.target.value })} placeholder="NMFC" />
                      </div>
                      <div className="freight-field" style={{ gridColumn: 'span 2' }}>
                        <label>Piece Type <span className="freight-muted">(optional)</span></label>
                        <input className="freight-input" value={c.pieceType ?? ''} onChange={(e) => updateCommodity(hu.id, c.id, { pieceType: e.target.value })} placeholder="Piece Type" />
                      </div>
                      <div className="freight-field" style={{ gridColumn: 'span 2' }}>
                        <label># of Pieces <span className="freight-muted">(optional)</span></label>
                        <input className="freight-input" value={c.pieces ?? ''} onChange={(e) => updateCommodity(hu.id, c.id, { pieces: e.target.value })} placeholder="# of Pieces" />
                      </div>
                      <div className="freight-field" style={{ gridColumn: 'span 10' }}>
                        <label>Description <span className="freight-muted">(optional)</span></label>
                        <input className="freight-input" value={c.description ?? ''} onChange={(e) => updateCommodity(hu.id, c.id, { description: e.target.value })} placeholder="Description" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="freight-card">
          <div className="freight-card-header">
            <h2>Where is your shipment going?</h2>
            <label className="freight-pill" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!freight?.skipAddressVerification}
                onChange={(e) => setFreight((prev) => ({ ...prev, skipAddressVerification: e.target.checked }))}
                style={{ margin: 0 }}
              />
              Skip Address Verification
            </label>
          </div>
          <div className="freight-card-body">
            <div className="freight-grid" style={{ marginBottom: 12 }}>
              <div className="freight-field" style={{ gridColumn: 'span 4' }}>
                <label>Ship Date</label>
                <input
                  type="date"
                  className="freight-input"
                  value={freight?.shipDate || todayYmd()}
                  onChange={(e) => setFreight((prev) => ({ ...prev, shipDate: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {renderAddressBlock('freight', 'sender', freight?.sender || emptyAddress(), setFreightAddrField, freightErrors)}
              {renderAddressBlock('freight', 'receiver', freight?.receiver || emptyAddress(), setFreightAddrField, freightErrors)}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div className="freight-card">
          <div className="freight-card-header">
            <h2>Shipment Summary</h2>
          </div>
          <div className="freight-card-body" style={{ padding: 0 }}>
            {(freight?.handlingUnits || []).map((hu, idx) => (
              <div key={hu.id} className="freight-summary-item">
                <div className="freight-summary-title">Handling Unit {idx + 1}</div>
                <div className="freight-muted">
                  {hu.type || 'Pallet'} • {hu.stackable ? 'Stackable' : 'Do not stack'}
                </div>
                <div className="freight-muted" style={{ marginTop: 4 }}>
                  {(hu.lengthIn && hu.widthIn) ? `${hu.lengthIn} x ${hu.widthIn}${hu.heightIn ? ` x ${hu.heightIn}` : ''} in.` : 'N/A'}
                </div>
              </div>
            ))}
          </div>

          <details className="freight-collapsible">
            <summary>In Progress</summary>
            <div className="freight-card-body">
              {freightLastQuote?.quoteId ? (
                <div>
                  <div><b>Last quote:</b> #{freightLastQuote.quoteId}</div>
                  <div className="freight-muted">Offers: {freightLastQuote.offersCount ?? 0}</div>
                  {freightLastQuote.externalTransactionId && (
                    <div className="freight-muted">Transaction: {freightLastQuote.externalTransactionId}</div>
                  )}
                </div>
              ) : (
                <div className="freight-muted">Saved drafts and quote history will appear here.</div>
              )}
            </div>
          </details>
          <details className="freight-collapsible">
            <summary>Favorites</summary>
            <div className="freight-card-body">
              <div className="freight-muted">Favorite addresses and commodities will appear here.</div>
            </div>
          </details>
          <details className="freight-collapsible">
            <summary>Address Notes</summary>
            <div className="freight-card-body">
              <textarea
                className="freight-textarea"
                value={freight?.addressNotes || ''}
                onChange={(e) => setFreight((prev) => ({ ...prev, addressNotes: e.target.value }))}
                placeholder="Driver instructions, gate code, dock notes, etc."
              />
            </div>
          </details>
        </div>
      </div>
    </div>
  );

  const renderParcelTab = () => (
    <div className="freight-layout">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div className="freight-card">
          <div className="freight-card-header">
            <h2>Shipment Addresses</h2>
            <label className="freight-pill" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!parcel?.skipAddressVerification}
                onChange={(e) => setParcel((prev) => ({ ...prev, skipAddressVerification: e.target.checked }))}
                style={{ margin: 0 }}
              />
              Skip Address Verification
            </label>
          </div>
          <div className="freight-card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {renderAddressBlock('parcel', 'sender', parcel?.sender || emptyAddress(), setParcelAddrField, parcelErrors)}
              {renderAddressBlock('parcel', 'receiver', parcel?.receiver || emptyAddress(), setParcelAddrField, parcelErrors)}
            </div>
          </div>
        </div>

        <div className="freight-card">
          <div className="freight-card-header">
            <h2>Shipment Details</h2>
            <div className="freight-row-actions">
              <label className="freight-pill" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!parcel?.insureShipment}
                  onChange={(e) => setParcel((prev) => ({ ...prev, insureShipment: e.target.checked }))}
                  style={{ margin: 0 }}
                />
                Insure Shipment (coming soon)
              </label>
            </div>
          </div>
          <div className="freight-card-body">
            <div className="freight-grid">
              <div className="freight-field" style={{ gridColumn: 'span 6' }}>
                <label>Package Name <span className="freight-muted">(optional)</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="freight-input"
                    value={parcel?.package?.packageName || ''}
                    onChange={(e) => setParcel((prev) => ({ ...prev, package: { ...prev.package, packageName: e.target.value } }))}
                    placeholder="Package Name"
                  />
                  <button type="button" className="btn secondary" title="Search (coming soon)" onClick={() => showToast({ title: 'Coming soon', message: 'Package templates will be added.', variant: 'info' })}>
                    <FaSearch />
                  </button>
                </div>
              </div>
              <div className="freight-field" style={{ gridColumn: 'span 3' }}>
                <label>Total identical packages</label>
                <input
                  className="freight-input"
                  type="number"
                  min={1}
                  value={parcel?.package?.totalIdentical ?? 1}
                  onChange={(e) => setParcel((prev) => ({ ...prev, package: { ...prev.package, totalIdentical: Math.max(1, Number(e.target.value || 1)) } }))}
                />
              </div>
              <div className="freight-field" style={{ gridColumn: 'span 3' }}>
                <label>Package</label>
                <select
                  className="freight-select"
                  value={parcel?.package?.packageType || 'Custom'}
                  onChange={(e) => setParcel((prev) => ({ ...prev, package: { ...prev.package, packageType: e.target.value } }))}
                >
                  <option>Custom</option>
                  <option>UPS Letter</option>
                  <option>UPS Pak</option>
                  <option>FedEx Envelope</option>
                </select>
              </div>

              <div className="freight-field" style={{ gridColumn: 'span 3' }}>
                <label>Length (in.) *</label>
                <input
                  className={`freight-input ${getErr(parcelErrors, 'package.lengthIn') ? 'freight-field-error' : ''}`}
                  value={parcel?.package?.lengthIn || ''}
                  onChange={(e) => setParcel((prev) => ({ ...prev, package: { ...prev.package, lengthIn: e.target.value } }))}
                />
                {getErr(parcelErrors, 'package.lengthIn') && <div className="freight-error-text">{getErr(parcelErrors, 'package.lengthIn')}</div>}
              </div>
              <div className="freight-field" style={{ gridColumn: 'span 3' }}>
                <label>Width (in.) *</label>
                <input
                  className={`freight-input ${getErr(parcelErrors, 'package.widthIn') ? 'freight-field-error' : ''}`}
                  value={parcel?.package?.widthIn || ''}
                  onChange={(e) => setParcel((prev) => ({ ...prev, package: { ...prev.package, widthIn: e.target.value } }))}
                />
                {getErr(parcelErrors, 'package.widthIn') && <div className="freight-error-text">{getErr(parcelErrors, 'package.widthIn')}</div>}
              </div>
              <div className="freight-field" style={{ gridColumn: 'span 3' }}>
                <label>Height (in.) *</label>
                <input
                  className={`freight-input ${getErr(parcelErrors, 'package.heightIn') ? 'freight-field-error' : ''}`}
                  value={parcel?.package?.heightIn || ''}
                  onChange={(e) => setParcel((prev) => ({ ...prev, package: { ...prev.package, heightIn: e.target.value } }))}
                />
                {getErr(parcelErrors, 'package.heightIn') && <div className="freight-error-text">{getErr(parcelErrors, 'package.heightIn')}</div>}
              </div>
              <div className="freight-field" style={{ gridColumn: 'span 3' }}>
                <label>Weight (lbs) *</label>
                <input
                  className={`freight-input ${getErr(parcelErrors, 'package.weightLbs') ? 'freight-field-error' : ''}`}
                  value={parcel?.package?.weightLbs || ''}
                  onChange={(e) => setParcel((prev) => ({ ...prev, package: { ...prev.package, weightLbs: e.target.value } }))}
                />
                {getErr(parcelErrors, 'package.weightLbs') && <div className="freight-error-text">{getErr(parcelErrors, 'package.weightLbs')}</div>}
              </div>

              <div className="freight-field" style={{ gridColumn: 'span 12' }}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {[
                    ['largePackage', 'Large Package'],
                    ['additionalHandling', 'Additional Handling'],
                    ['savePackage', 'Save Package'],
                  ].map(([k, lbl]) => (
                    <label key={k} className="freight-pill" style={{ cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!parcel?.package?.[k]}
                        onChange={(e) => setParcel((prev) => ({ ...prev, package: { ...prev.package, [k]: e.target.checked } }))}
                        style={{ margin: 0 }}
                      />
                      {lbl}
                    </label>
                  ))}
                </div>
              </div>

              <div className="freight-field" style={{ gridColumn: 'span 12' }}>
                <label>Reference 1 <span className="freight-muted">(optional)</span></label>
                <input className="freight-input" value={parcel?.references?.ref1 || ''} onChange={(e) => setParcel((prev) => ({ ...prev, references: { ...prev.references, ref1: e.target.value } }))} placeholder="Reference Value" />
              </div>
              <div className="freight-field" style={{ gridColumn: 'span 12' }}>
                <label>Reference 2 <span className="freight-muted">(optional)</span></label>
                <input className="freight-input" value={parcel?.references?.ref2 || ''} onChange={(e) => setParcel((prev) => ({ ...prev, references: { ...prev.references, ref2: e.target.value } }))} placeholder="Reference Value" />
              </div>
              <div className="freight-field" style={{ gridColumn: 'span 12' }}>
                <label className="freight-pill" style={{ cursor: 'pointer', width: 'fit-content' }}>
                  <input
                    type="checkbox"
                    checked={!!parcel?.references?.printRef1Barcode}
                    onChange={(e) => setParcel((prev) => ({ ...prev, references: { ...prev.references, printRef1Barcode: e.target.checked } }))}
                    style={{ margin: 0 }}
                  />
                  Print Reference 1 as Barcode (coming soon)
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div className="freight-card">
          <div className="freight-card-header">
            <h2>Additional Services</h2>
          </div>
          <div className="freight-card-body">
            {[
              ['handlingCharge', 'Handling Charge'],
              ['carbonNeutral', 'UPS Carbon Neutral'],
              ['returnLabel', 'Return Label'],
              ['shipmentAlerts', 'Shipment Alerts'],
              ['notifications', 'UPS Notifications'],
              ['includeSendersReceipt', "Include Sender's Receipt"],
              ['emailLabel', 'Email Label'],
            ].map(([k, lbl]) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!parcel?.additionalServices?.[k]}
                  onChange={(e) => setParcel((prev) => ({ ...prev, additionalServices: { ...prev.additionalServices, [k]: e.target.checked } }))}
                />
                <span>{lbl}</span>
              </label>
            ))}

            <div className="freight-divider" />
            <div className="freight-field">
              <label>Signature Options</label>
              <select className="freight-select" value={parcel?.signatureOption || 'No Signature Option'} onChange={(e) => setParcel((prev) => ({ ...prev, signatureOption: e.target.value }))}>
                <option>No Signature Option</option>
                <option>Signature Required</option>
                <option>Adult Signature Required</option>
              </select>
            </div>
            <div className="freight-field" style={{ marginTop: 10 }}>
              <label>Select Billing Details</label>
              <select className="freight-select" value={parcel?.billingDetails || 'Bill My Account'} onChange={(e) => setParcel((prev) => ({ ...prev, billingDetails: e.target.value }))}>
                <option>Bill My Account</option>
                <option>Bill Recipient</option>
                <option>Bill Third Party</option>
              </select>
            </div>
          </div>
        </div>

        <div className="freight-card">
          <div className="freight-card-header">
            <h2>Service Type</h2>
          </div>
          <div className="freight-card-body">
            <div className="freight-field">
              <label>Ship Date</label>
              <input type="date" className="freight-input" value={parcel?.shipDate || todayYmd()} onChange={(e) => setParcel((prev) => ({ ...prev, shipDate: e.target.value }))} />
            </div>
            <div style={{ marginTop: 12 }}>
              <button type="button" className="btn secondary" onClick={() => showToast({ title: 'Coming soon', message: 'Pickup scheduling will be added.', variant: 'info' })}>
                <FaPlus /> Pickup
              </button>
            </div>
            <div className="freight-divider" />
            {parcelLastQuote?.quoteId ? (
              <div>
                <div><b>Last quote:</b> #{parcelLastQuote.quoteId}</div>
                <div className="freight-muted">Offers: {parcelLastQuote.offersCount ?? 0}</div>
              </div>
            ) : (
              <div className="freight-muted">Carrier/service results will display here.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="freight-page">
      <div className="freight-tabs" role="tablist" aria-label="Shipping tabs">
        <button
          type="button"
          className={`freight-tab ${tab === 'parcel' ? 'active' : ''}`}
          role="tab"
          aria-selected={tab === 'parcel'}
          onClick={() => setTab('parcel')}
        >
          Small Package
        </button>
        <button
          type="button"
          className={`freight-tab ${tab === 'freight' ? 'active' : ''}`}
          role="tab"
          aria-selected={tab === 'freight'}
          onClick={() => setTab('freight')}
        >
          Freight
        </button>
      </div>

      <div className="freight-scroll">
        {tab === 'freight' ? renderFreightTab() : renderParcelTab()}
      </div>

      <div className="freight-actionbar">
        <div className="freight-actions-left">
          <div className="freight-row-actions" style={{ gap: 10 }}>
            <span className="freight-muted">Provider</span>
            <select
              className="freight-select"
              value={unishippersIntegrationId}
              onChange={(e) => setUnishippersIntegrationId(e.target.value)}
              style={{ padding: '0.35rem 0.5rem', borderRadius: 8 }}
            >
              {unishippersProviders.length ? (
                unishippersProviders.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.displayname || `Unishippers #${p.id}`} ({p.environment || 'sandbox'})
                  </option>
                ))
              ) : (
                <option value="">No Unishippers integration</option>
              )}
            </select>
            <span className="freight-muted">
              {tab === 'freight'
                ? `${freightSummary.huCount} handling unit(s) • ${freightSummary.commodityCount} commodity line(s) • ${freightSummary.totalWeight.toFixed(0)} lbs`
                : 'Build a parcel shipment and request rates.'}
            </span>
          </div>
        </div>
        <div className="freight-actions-right">
          <button className="btn secondary" type="button" onClick={resetDraft} title="Reset this draft">
            <FaTrash /> Reset
          </button>

          {tab === 'freight' ? (
            <button className="btn primary" type="button" onClick={handleGetFreightQuote} title="Get freight quote" disabled={freightQuoting}>
              <FaTruck /> Get Quote
            </button>
          ) : (
            <>
              <button className="btn secondary" type="button" onClick={handleQuickParcelQuote} title="Quick quote" disabled={parcelQuoting}>
                <FaTruck /> Quick Quote
              </button>
              <button className="btn secondary" type="button" onClick={() => showToast({ title: 'Saved', message: 'Draft saved locally.', variant: 'info' })}>
                <FaSave /> Save Progress
              </button>
              <button className="btn secondary" type="button" onClick={() => showToast({ title: 'Coming soon', message: 'Shipment review will be added.', variant: 'info' })}>
                <FaFileAlt /> Review Shipment
              </button>
              <button className="btn primary" type="button" onClick={() => showToast({ title: 'Coming soon', message: 'Label printing will be added.', variant: 'info' })}>
                <FaPrint /> Print Label
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
